import React from 'react';
import type { Page, Component } from '../ComponentEditModal.types';
import { PageSelector } from '../shared/PageSelector';
import { RichTextEditor } from '../../RichTextEditor';
import { ColorCustomizationPanel } from '../shared/ColorInput';
import { getColorFieldsForComponent } from '../shared/colorFieldUtils';
import { useCustomColors } from '../../../contexts/CustomColorsContext';

interface TitleSubtitleData {
  title?: string;
  titleTag?: string;
  titleAlignment?: string;
  titleColor?: string;
  titleFontWeight?: string;
  titleFontSize?: string;
  subtitle?: string;
  subtitleTag?: string;
  subtitleAlignment?: string;
  subtitleColor?: string;
  subtitleFontWeight?: string;
  subtitleFontSize?: string;
  spacing?: string;
  button?: {
    enabled?: boolean;
    text?: string;
    url?: string;
    style?: string;
    target?: string;
  };
  customColors?: {
    background?: string;
    title?: string;
    subtitle?: string;
    buttonBg?: string;
    buttonText?: string;
  };
}

interface TitleSubtitleEditorProps {
  formData: TitleSubtitleData;
  setFormData: (data: TitleSubtitleData) => void;
  pages: Page[];
  isLoadingPages: boolean;
  currentPageComponents?: Component[];
}

export const TitleSubtitleEditor: React.FC<TitleSubtitleEditorProps> = ({
  formData,
  setFormData,
  pages,
  isLoadingPages,
  currentPageComponents,
}) => {
  const data = formData;
  const themeColors = useCustomColors();

  const updateCustomColor = (key: string, value: string | null) => {
    setFormData({
      ...data,
      customColors: { ...data.customColors, [key]: value },
    });
  };

  // Get theme-aware color defaults
  const colorFields = getColorFieldsForComponent('title-subtitle', themeColors);

  return (
    <div className="space-y-8">
      {/* Title Section */}
      <TitleSection data={data} setFormData={setFormData} />

      {/* Subtitle Section */}
      <SubtitleSection data={data} setFormData={setFormData} />

      {/* Spacing Section */}
      <SpacingSection data={data} setFormData={setFormData} />

      {/* Button Section */}
      <ButtonSection
        data={data}
        setFormData={setFormData}
        pages={pages}
        isLoadingPages={isLoadingPages}
        currentPageComponents={currentPageComponents}
      />

      {/* Color Customization */}
      <ColorCustomizationPanel
        title="Colors (Optional)"
        description="Customize title-subtitle colors. Leave empty to use defaults."
        customColors={data.customColors as Record<string, string | undefined>}
        colorFields={colorFields}
        onUpdateColor={updateCustomColor}
        onClearAllColors={() => setFormData({ ...data, customColors: undefined })}
      />
    </div>
  );
};

// Sub-components

interface TitleSectionProps {
  data: TitleSubtitleData;
  setFormData: (data: TitleSubtitleData) => void;
}

const TitleSection: React.FC<TitleSectionProps> = ({ data, setFormData }) => (
  <div>
    <h3 className="text-lg font-medium text-gray-900 mb-4">Title</h3>
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Title Text</label>
        <input
          type="text"
          value={data.title || ''}
          onChange={e => setFormData({ ...data, title: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          placeholder="Enter title text (optional)"
          maxLength={300}
        />
        <p className="text-xs text-gray-500 mt-1">{(data.title || '').length} / 300 characters</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Title HTML Tag</label>
          <select
            value={data.titleTag || 'h2'}
            onChange={e => setFormData({ ...data, titleTag: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="h2">H2</option>
            <option value="h3">H3</option>
            <option value="h4">H4</option>
            <option value="h5">H5</option>
            <option value="h6">H6</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Title Alignment</label>
          <select
            value={data.titleAlignment || 'center'}
            onChange={e => setFormData({ ...data, titleAlignment: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="left">Left</option>
            <option value="center">Center</option>
            <option value="right">Right</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Title Color</label>
          <input
            type="color"
            value={data.titleColor || '#1F2937'}
            onChange={e => setFormData({ ...data, titleColor: e.target.value })}
            className="w-full h-10 px-1 py-1 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          />
          <p className="text-xs text-gray-500 mt-1">{data.titleColor || '#1F2937'}</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Title Font Weight</label>
          <select
            value={data.titleFontWeight || 'bold'}
            onChange={e => setFormData({ ...data, titleFontWeight: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="light">Light</option>
            <option value="normal">Normal</option>
            <option value="bold">Bold</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Title Font Size</label>
        <input
          type="text"
          value={data.titleFontSize || ''}
          onChange={e => setFormData({ ...data, titleFontSize: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          placeholder="e.g., 2rem, 32px, 3xl (optional)"
        />
        <p className="text-xs text-gray-500 mt-1">
          Leave empty to use default size based on HTML tag
        </p>
      </div>
    </div>
  </div>
);

interface SubtitleSectionProps {
  data: TitleSubtitleData;
  setFormData: (data: TitleSubtitleData) => void;
}

const SubtitleSection: React.FC<SubtitleSectionProps> = ({ data, setFormData }) => (
  <div>
    <h3 className="text-lg font-medium text-gray-900 mb-4">Subtitle</h3>
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle Text</label>
        <RichTextEditor
          content={data.subtitle || ''}
          onChange={html => setFormData({ ...data, subtitle: html })}
          placeholder="Enter subtitle text (optional)"
          minHeight="120px"
        />
        <p className="text-xs text-gray-500 mt-1">Use the toolbar to format your text</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle HTML Tag</label>
          <select
            value={data.subtitleTag || 'p'}
            onChange={e => setFormData({ ...data, subtitleTag: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="p">Paragraph (p)</option>
            <option value="h3">H3</option>
            <option value="h4">H4</option>
            <option value="h5">H5</option>
            <option value="h6">H6</option>
            <option value="span">Span</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle Alignment</label>
          <select
            value={data.subtitleAlignment || 'center'}
            onChange={e => setFormData({ ...data, subtitleAlignment: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="left">Left</option>
            <option value="center">Center</option>
            <option value="right">Right</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle Color</label>
          <input
            type="color"
            value={data.subtitleColor || '#6B7280'}
            onChange={e => setFormData({ ...data, subtitleColor: e.target.value })}
            className="w-full h-10 px-1 py-1 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          />
          <p className="text-xs text-gray-500 mt-1">{data.subtitleColor || '#6B7280'}</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Subtitle Font Weight
          </label>
          <select
            value={data.subtitleFontWeight || 'normal'}
            onChange={e => setFormData({ ...data, subtitleFontWeight: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="light">Light</option>
            <option value="normal">Normal</option>
            <option value="bold">Bold</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle Font Size</label>
        <input
          type="text"
          value={data.subtitleFontSize || ''}
          onChange={e => setFormData({ ...data, subtitleFontSize: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          placeholder="e.g., 1.25rem, 20px, xl (optional)"
        />
        <p className="text-xs text-gray-500 mt-1">
          Leave empty to use default size based on HTML tag
        </p>
      </div>
    </div>
  </div>
);

interface SpacingSectionProps {
  data: TitleSubtitleData;
  setFormData: (data: TitleSubtitleData) => void;
}

const SpacingSection: React.FC<SpacingSectionProps> = ({ data, setFormData }) => (
  <div>
    <h3 className="text-lg font-medium text-gray-900 mb-4">Spacing</h3>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Spacing Between Title and Subtitle
      </label>
      <select
        value={data.spacing || 'normal'}
        onChange={e => setFormData({ ...data, spacing: e.target.value })}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
      >
        <option value="tight">Tight (0.5rem)</option>
        <option value="normal">Normal (1rem)</option>
        <option value="loose">Loose (2rem)</option>
      </select>
      <p className="text-xs text-gray-500 mt-1">
        Only applies when both title and subtitle are present
      </p>
    </div>
  </div>
);

interface ButtonSectionProps {
  data: TitleSubtitleData;
  setFormData: (data: TitleSubtitleData) => void;
  pages: Page[];
  isLoadingPages: boolean;
  currentPageComponents?: Component[];
}

const ButtonSection: React.FC<ButtonSectionProps> = ({
  data,
  setFormData,
  pages,
  isLoadingPages,
  currentPageComponents,
}) => (
  <div>
    <div className="flex justify-between items-center mb-4">
      <h3 className="text-lg font-medium text-gray-900">Call-to-Action Button</h3>
      <label className="flex items-center space-x-2 cursor-pointer">
        <input
          type="checkbox"
          checked={data.button?.enabled || false}
          onChange={e =>
            setFormData({
              ...data,
              button: {
                ...data.button,
                enabled: e.target.checked,
                text: data.button?.text || 'Learn More',
                url: data.button?.url || '',
                style: data.button?.style || 'primary',
                target: data.button?.target || '_self',
              },
            })
          }
          className="w-4 h-4 text-primary-600 border-gray-300 rounded-md focus:ring-primary-500"
        />
        <span className="text-sm text-gray-600">Show button</span>
      </label>
    </div>

    {data.button?.enabled && (
      <div className="p-4 border border-gray-200 rounded-lg space-y-3 bg-gray-50">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-2">Button Text</label>
          <input
            type="text"
            value={data.button?.text || ''}
            onChange={e =>
              setFormData({
                ...data,
                button: { ...data.button, text: e.target.value },
              })
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 bg-white"
            placeholder="e.g., Learn More"
            maxLength={100}
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-2">Button URL</label>
          <PageSelector
            value={data.button?.url || ''}
            onChange={url =>
              setFormData({
                ...data,
                button: { ...data.button, url },
              })
            }
            pages={pages}
            isLoadingPages={isLoadingPages}
            currentPageComponents={currentPageComponents}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">Style</label>
            <select
              value={data.button?.style || 'primary'}
              onChange={e =>
                setFormData({
                  ...data,
                  button: { ...data.button, style: e.target.value },
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 bg-white"
            >
              <option value="primary">Primary</option>
              <option value="secondary">Secondary</option>
              <option value="outline">Outline</option>
              <option value="ghost">Ghost</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">Open In</label>
            <select
              value={data.button?.target || '_self'}
              onChange={e =>
                setFormData({
                  ...data,
                  button: { ...data.button, target: e.target.value },
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 bg-white"
            >
              <option value="_self">Same Tab</option>
              <option value="_blank">New Tab</option>
            </select>
          </div>
        </div>
      </div>
    )}
  </div>
);
